// =============================================================================
// Wave 3.37 — ImageUploadInput
// -----------------------------------------------------------------------------
// A minimal, drop-in image picker that:
//   * reads a file via FileReader into a base64 data URL
//   * enforces a configurable size limit (default 2 MB — matches backend cap)
//   * shows a thumbnail preview and a Remove button when a value is set
//   * exposes the data URL through the onChange callback (parent owns state)
//
// The component intentionally has no validation against file type — the
// browser file picker already handles `accept="image/*"`. Backend rejects
// non-data URLs.
// =============================================================================
import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ImagePlus, X } from 'lucide-react';
import { toast } from 'sonner';

export function ImageUploadInput({
  value,
  onChange,
  label = 'Image',
  helpText,
  maxMb = 2,
  testIdPrefix = 'image-upload',
  className = '',
}) {
  const inputRef = useRef(null);

  const handleFile = (file) => {
    if (!file) return;
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(`Image must be ${maxMb} MB or smaller`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result || '');
    reader.onerror = () => toast.error('Could not read the selected image');
    reader.readAsDataURL(file);
  };

  const onPick = (e) => {
    const f = e?.target?.files?.[0];
    if (f) handleFile(f);
    // Reset so picking the same file again still fires onChange.
    if (inputRef.current) inputRef.current.value = '';
  };

  const onClear = () => onChange('');

  return (
    <div className={className}>
      {label && <Label>{label}</Label>}
      <div className="mt-1 flex items-start gap-3">
        {value ? (
          <div className="relative shrink-0">
            <img
              src={value}
              alt="preview"
              className="w-24 h-24 object-cover rounded-md border border-[var(--divider)] bg-slate-50"
              data-testid={`${testIdPrefix}-preview`}
            />
            <button
              type="button"
              onClick={onClear}
              className="absolute -top-2 -right-2 bg-white border border-slate-300 rounded-full p-0.5 shadow-sm hover:bg-rose-50"
              title="Remove image"
              data-testid={`${testIdPrefix}-clear`}
            >
              <X size={12} className="text-rose-600" />
            </button>
          </div>
        ) : (
          <div
            className="w-24 h-24 rounded-md border border-dashed border-slate-300 bg-slate-50/60 flex items-center justify-center text-slate-400 shrink-0"
            data-testid={`${testIdPrefix}-empty`}
          >
            <ImagePlus size={20} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onPick}
            data-testid={`${testIdPrefix}-file-input`}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            data-testid={`${testIdPrefix}-choose`}
          >
            {value ? 'Replace image' : 'Choose image'}
          </Button>
          {helpText && (
            <div className="text-[11px] text-slate-500 mt-1">{helpText}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ImageUploadInput;
